import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage } from "../../offlineQueue";

// Texas Scramble team allowance: 25% of lowest HCP, 20% of 2nd lowest, 15% of 3rd, 10% of highest

// CONGU/WHS scramble allowance table varies by team size — was hardcoded
// for 4 players only. Now adapts to the group's actual member count, so an
// odd-sized group (e.g. a 3-ball among mostly 4-balls) still gets the
// correct table automatically regardless of which scramble format was
// selected at event setup.
function getScramblePcts(count) {
  if (count === 3) return [0.30, 0.20, 0.10];
  if (count === 2) return [0.35, 0.15];
  return [0.25, 0.20, 0.15, 0.10];
}
const calcScrambleAllowance = (members) => {
  const pcts = getScramblePcts(members.length);
  const sorted = [...members].sort((a, b) => ((a.hcp_adj ?? a.handicap) || 0) - ((b.hcp_adj ?? b.handicap) || 0));
  return Math.round(sorted.reduce((sum, p, i) => sum + ((p.hcp_adj ?? p.handicap) || 0) * (pcts[i] || 0), 0));
};

export default function TeamAggregateScoreEntry() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => { loadAll(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }

      const groupRes = await api.get("/groups");
      const players = (groupRes.data.players || []).filter(p => p.playing);

      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = [];
        grpMap[p.grp].push(p);
      });

      const teamsArr = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, members]) => {
        const combHcp = members.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
        const scrambleAllowance = calcScrambleAllowance(members);
        const playerHcps = members.map(p => `${p.name.split(" ")[0]} (${p.hcp_adj ?? p.handicap})`).join(", ");
        teamsArr.push({
          grp: parseInt(grp),
          label: `Group ${grp}: ${members.map(p => p.name.split(" ")[0]).join("/")}`,
          members,
          playerHcps,
          combHcp,
          scrambleAllowance,
          leadPlayerId: members[0].player_id,
        });
      });

      setTeams(teamsArr);
      if (teamsArr.length > 0) setSelectedTeam(teamsArr[0]);

      const scoresRes = await api.get("/scores/" + eventRes.data.id);
      const map = {};
      scoresRes.data.forEach(s => { map[s.player_id + "_" + s.hole] = s.gross; });
      setScores(map);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleScore = (playerId, hole, val) => {
    setScores(prev => ({ ...prev, [playerId + "_" + hole]: val }));
  };

  const getScore = (playerId, hole) => scores[playerId + "_" + hole] || "";

  const getScrambleShots = (hole) => {
    if (!selectedTeam) return 0;
    const allowance = selectedTeam.scrambleAllowance;
    const base = Math.floor(allowance / 18);
    const extra = allowance % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };

  // Texas Scramble traditionally scores by net strokes, not points --
  // "Texas Scramble Medal" formats (added 2026-09-09) score that way; the
  // original points-based "Texas Scramble" formats are unchanged.
  const isMedal = event?.game_type?.includes("Medal");

  const getTeamScore = (hole) => {
    if (!selectedTeam) return "";
    const gross = parseInt(getScore(selectedTeam.leadPlayerId, hole.hole));
    if (!gross) return "";
    const shots = getScrambleShots(hole);
    const net = gross - shots;
    return isMedal ? net : Math.max(0, 2 + (hole.par - net));
  };

  const getRunningTotal = (upToIndex) => {
    let total = 0;
    for (let i = 0; i <= upToIndex; i++) {
      const h = holes[i];
      if (!h) continue;
      const score = getTeamScore(h);
      if (score === "") continue;
      total += parseInt(score);
    }
    return total || "";
  };

  const saveScores = async () => {
    if (!event || !selectedTeam) return;
    try {
      const allScores = [];
      holes.forEach(h => {
        const gross = parseInt(getScore(selectedTeam.leadPlayerId, h.hole));
        if (gross) allScores.push({ player_id: selectedTeam.leadPlayerId, hole: h.hole, gross });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const randomScores = () => {
    if (!selectedTeam) return;
    const newScores = { ...scores };
    holes.forEach(h => { newScores[selectedTeam.leadPlayerId + "_" + h.hole] = h.par + Math.floor(Math.random() * 3); });
    setScores(newScores);
    showToast("Random scores generated");
  };

  const randomAllGroups = async () => {
    if (!event || teams.length === 0) return;
    try {
      const allScores = [];
      teams.forEach(team => {
        holes.forEach(h => { allScores.push({ player_id: team.leadPlayerId, hole: h.hole, gross: h.par + Math.floor(Math.random() * 3) }); });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      showToast(queuedMessage(res, "Random scores generated for all " + teams.length + " groups"));
      const scoresRes = await api.get("/scores/" + event.id);
      const map = {};
      scoresRes.data.forEach(s => { map[s.player_id + "_" + s.hole] = s.gross; });
      setScores(map);
    } catch(e) { showToast("Failed: " + e.message); }
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Texas Scramble Score Entry</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveScores} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={randomScores} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
            <button type="button" onClick={randomAllGroups} className="bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random ALL Groups</button>
          </div>
        </div>

        <div className="bg-yellow-50 border-b border-gray-300 px-4 py-2 flex items-center gap-4 flex-wrap">
          <span className="text-xs font-medium text-gray-600">Select Group:</span>
          <select value={selectedTeam ? selectedTeam.label : ""}
            onChange={e => setSelectedTeam(teams.find(t => t.label === e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-xs w-64">
            {teams.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
          </select>
          {selectedTeam && (
            <span className="text-xs text-gray-600">
              Course HCP: <strong>{selectedTeam.playerHcps}</strong> &nbsp;|&nbsp; Combined Course HCP: <strong>{selectedTeam.combHcp}</strong> &nbsp;|&nbsp; Allowance (25/20/15/10%): <strong>{selectedTeam.scrambleAllowance}</strong>
            </span>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-left w-16">Hole</th>
                <th className="px-4 py-2 text-center w-16">Par</th>
                <th className="px-4 py-2 text-center w-16">SI</th>
                <th className="px-4 py-2 text-center w-36">Team Gross</th>
                <th className="px-4 py-2 text-center w-16">HCP Shots</th>
                <th className="px-4 py-2 text-center w-16">Net</th>
                <th className="px-4 py-2 text-center w-20">{isMedal ? "Running Net" : "Running Pts"}</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((hole, i) => {
                const gross = parseInt(getScore(selectedTeam?.leadPlayerId, hole.hole)) || "";
                const shots = getScrambleShots(hole);
                const net = gross ? gross - shots : "";
                const running = getRunningTotal(i);
                return (
                  <tr key={hole.hole} className={"border-b border-gray-200 " + (i%2===0 ? "bg-white" : "bg-green-50")}>
                    <td className="px-4 py-1.5 font-medium">{hole.hole}</td>
                    <td className="px-4 py-1.5 text-center">{hole.par}</td>
                    <td className="px-4 py-1.5 text-center text-gray-500">{hole.stroke_index}</td>
                    <td className="px-4 py-1.5 text-center">
                      <input type="number" min="1" max="15"
                        value={gross}
                        onChange={e => selectedTeam && handleScore(selectedTeam.leadPlayerId, hole.hole, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-0.5 w-24 text-xs text-center focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-4 py-1.5 text-center font-medium text-green-700">{shots}</td>
                    <td className="px-4 py-1.5 text-center">{net}</td>
                    <td className="px-4 py-1.5 text-center font-bold">{running}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && holes.length === 0 && (
            <div className="text-center py-8 text-gray-500">No course loaded. Go to Course page and select a course.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
